import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/common/Navbar';
import { useInvoices } from '../../hooks/useInvoice';
import { useOrdererOrders } from '../../hooks/useOrdererOrders';
import { orderTitle } from '../../utils/orderTitle';
import DirectInvoicesSection from '../../components/orderer/DirectInvoicesSection';
import { COLORS, FONTS } from '../../styles/tokens';
import { TruckLoader } from '../../components/common/truck';

const kr = n => `kr ${Number(n ?? 0).toLocaleString('nb-NO')}`;

// Betalingsstatus på faktura slik bestilleren ser den
const STATUS = {
  pending:    { text: 'Behandles',  bg: COLORS.purpleBg,  color: COLORS.purpleText },
  authorized: { text: 'Autorisert', bg: COLORS.blueBg,    color: COLORS.blueText },
  captured:   { text: 'Betalt',     bg: COLORS.successBg, color: COLORS.successText },
  paid_out:   { text: 'Betalt',     bg: COLORS.successBg, color: COLORS.successText },
  failed:     { text: 'Feilet',     bg: COLORS.dangerBg,  color: COLORS.dangerText },
};

function describe(order) {
  return order ? orderTitle(order) : null;
}

export default function InvoicesPage() {
  const { invoices, loading } = useInvoices();
  const { orders } = useOrdererOrders();
  const navigate = useNavigate();

  const orderById = useMemo(() => new Map(orders.map(o => [o.id, o])), [orders]);

  const now = new Date();
  const monthName = now.toLocaleDateString('nb-NO', { month: 'long' });
  const monthTotal = invoices
    .filter(i => {
      const d = i.createdAt?.toDate?.();
      return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((s, i) => s + (i.totalAmount ?? 0), 0);

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
        <h1 style={styles.title}>Fakturaer</h1>

        <div style={styles.hero}>
          <div style={styles.heroLabel}>Forbruk i {monthName}, eks. mva</div>
          <div style={styles.heroAmount}>{kr(monthTotal)}</div>
        </div>

        <h2 style={styles.h2}>Siste fakturaer</h2>

        {loading ? (
          <TruckLoader showLabel label="Laster fakturaer…" />
        ) : invoices.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyTitle}>Ingen fakturaer ennå</div>
            <p style={styles.emptyText}>Fakturaer opprettes automatisk når oppdrag er fullført.</p>
          </div>
        ) : (
          invoices.map(inv => {
            const order = orderById.get(inv.orderId);
            const date = (inv.paidAt ?? inv.createdAt)?.toDate?.();
            const st = STATUS[inv.status] ?? { text: inv.status, bg: COLORS.surfaceHover, color: COLORS.textLight };
            return (
              <button key={inv.id} type="button" style={styles.row} onClick={() => navigate(`/orderer/order/${inv.orderId}`)}>
                <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <div style={styles.rowTitle}>{describe(order) ?? `Oppdrag #${inv.orderId?.slice(-6)}`}</div>
                  <div style={styles.rowMono}>
                    #{inv.orderId?.slice(-6)}{date && ` · ${date.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short' }).toUpperCase()}`}
                  </div>
                  {inv.adjustmentAmount !== 0 && inv.adjustmentAmount != null && (
                    <div style={styles.rowAdj}>Inkl. justering {inv.adjustmentAmount > 0 ? '+' : '−'} {kr(Math.abs(inv.adjustmentAmount))}</div>
                  )}
                </div>
                <div style={styles.rowRight}>
                  <div style={styles.rowAmount}>{Number(inv.totalAmount ?? 0).toLocaleString('nb-NO')}</div>
                  <span style={{ ...styles.badge, background: st.bg, color: st.color }}>{st.text}</span>
                </div>
              </button>
            );
          })
        )}

        <DirectInvoicesSection />
      </div>
    </div>
  );
}

const styles = {
  root:       { minHeight: '100vh', background: COLORS.bg, display: 'flex', flexDirection: 'column' },
  page:       { maxWidth: 720, margin: '0 auto', padding: '24px 20px', width: '100%', paddingTop: 'max(32px, env(safe-area-inset-top))' },
  title:      { fontFamily: FONTS.display, fontSize: 40, lineHeight: 1, fontWeight: 700, margin: '12px 0 14px', color: COLORS.textDark },
  hero:       { background: COLORS.brandPanel, color: '#fff', borderRadius: 22, padding: 20, marginBottom: 8 },
  heroLabel:  { fontSize: 13, color: 'rgba(255,255,255,0.65)' },
  heroAmount: { fontFamily: FONTS.display, fontWeight: 700, fontSize: 52, lineHeight: 1, color: COLORS.accent, marginTop: 4 },
  h2:         { fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, margin: '16px 0 2px', color: COLORS.textDark },
  row:        { display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 0', background: 'none', border: 'none', borderBottom: `1px solid ${COLORS.border}`, cursor: 'pointer', fontFamily: 'inherit', color: COLORS.textDark },
  rowTitle:   { fontWeight: 700, fontSize: 15 },
  rowMono:    { fontFamily: 'var(--font-mono)', fontSize: 10.5, color: COLORS.textLight, marginTop: 3, letterSpacing: '0.03em' },
  rowAdj:     { fontSize: 12, color: COLORS.warningText, marginTop: 3 },
  rowRight:   { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 },
  rowAmount:  { fontFamily: FONTS.display, fontWeight: 700, fontSize: 24, lineHeight: 1 },
  badge:      { borderRadius: 20, padding: '3px 10px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' },
  state:      { textAlign: 'center', color: COLORS.textLight, padding: 40 },
  empty:      { textAlign: 'center', padding: '48px 20px' },
  emptyTitle: { fontFamily: FONTS.display, fontSize: 28, fontWeight: 700, color: COLORS.textDark },
  emptyText:  { color: COLORS.textMid, fontSize: 14, marginTop: 6 },
};
